import { useQuery } from "@tanstack/react-query";
import {
  Link,
  useLocation,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { sortBy, unionBy } from "lodash-es";
import { ListOrderedIcon, MinusIcon, PlusIcon, Undo2Icon } from "lucide-react";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import {
  Button as AriaButton,
  DialogTrigger,
  Disclosure,
  DisclosureGroup,
  DisclosurePanel,
  Heading,
} from "react-aria-components";

import { Button, button } from "@/components/base/button";
import { MultiSelect } from "@/components/base/multi-select";
import { Popover } from "@/components/base/popover";
import { title } from "@/components/base/primitives";
import { Toolbar } from "@/components/base/toolbar";
import { TeamNames } from "@/components/teams/names";
import type { Tournament, TournamentDivision } from "@/db/schema";
import { getPoolStats } from "@/hooks/matches";
import { usePanelModals } from "@/hooks/panel-modals";
import {
  useCompletePoolsMutation,
  useSetDivisionStatusMutation,
} from "@/hooks/tournament-mutations";
import { useViewerPoolIds } from "@/hooks/viewer-pools";
import { useTRPC } from "@/integrations/trpc/react";
import { isNotNull } from "@/utils/types";

import {
  useCanUpdateTournament,
  useTeamMap,
  useTournamentDivision,
} from "../../context";
import { CompletePoolsForm } from "../../controls/complete-pools";
import { CreatePoolMatchesForm } from "../../controls/create-pool-matches";
import { PoolMatchGrid } from "./pool-match-grid";

export const GamesPanel = memo(function GamesPanel({
  id,
  tournamentDivisionId,
  isActive,
}: Pick<Tournament, "id"> & {
  tournamentDivisionId: TournamentDivision["id"];
  isActive: boolean;
}) {
  const trpc = useTRPC();

  const canEdit = useCanUpdateTournament();

  const search = useSearch({
    from: "/tournaments/$tournamentId/$divisionId/{-$tab}",
  });

  const pathname = useLocation({ select: (loc) => loc.pathname });

  const navigate = useNavigate({
    from: "/tournaments/$tournamentId/$divisionId/{-$tab}",
  });

  const { data, refetch } = useQuery({
    ...trpc.tournaments.getPools.queryOptions({
      tournamentDivisionId,
    }),
    enabled: isActive,
  });

  const { teamMap } = useTeamMap();

  const poolFilterOptions = useMemo(() => {
    return (
      data?.map((pool) => {
        return {
          display: pool.name.toUpperCase(),
          value: pool.id,
        };
      }) ?? []
    );
  }, [data]);

  const courtFilterOptions = useMemo(() => {
    return sortBy(data?.map((p) => p.court).filter(isNotNull) ?? [], (v) => {
      if (Number.isNaN(v)) {
        return v;
      }

      return Number.parseInt(v, 10);
    }).map((court) => {
      return {
        display: court,
        value: court,
      };
    });
  }, [data]);

  const { viewerPoolIds, isViewerPending } = useViewerPoolIds(data, teamMap);

  const poolFilterValues = useMemo(() => new Set(search.pools), [search.pools]);
  const courtFilterValues = useMemo(
    () => new Set(search.courts),
    [search.courts],
  );

  const [filterOpen, setFilterOpen] = useState(false);

  const visiblePools = useMemo(() => {
    const hasFilters = poolFilterValues.size > 0 || courtFilterValues.size > 0;

    const byPool =
      data?.filter((pool) =>
        hasFilters ? poolFilterValues.has(pool.id) : true,
      ) ?? [];

    const byCourt =
      data?.filter((pool) =>
        hasFilters
          ? pool.court
            ? courtFilterValues.has(pool.court)
            : false
          : true,
      ) ?? [];

    return unionBy(byPool, byCourt, (pool) => pool.id);
  }, [data, poolFilterValues, courtFilterValues]);

  // Auto-select the viewer's pool when they're a player (not a TD)
  const hasAppliedViewer = useRef(false);
  useEffect(() => {
    if (!data || isViewerPending || hasAppliedViewer.current) return;
    hasAppliedViewer.current = true;
    if (!canEdit && viewerPoolIds.size > 0 && poolFilterValues.size === 0) {
      navigate({
        replace: true,
        resetScroll: false,
        search: (prev) => ({
          ...prev,
          pools: [...viewerPoolIds],
        }),
      });
    }
  }, [
    data,
    isViewerPending,
    canEdit,
    viewerPoolIds,
    navigate,
    poolFilterValues.size,
  ]);

  const division = useTournamentDivision();
  const { setActiveModal, modalProps } = usePanelModals<
    "completePools" | "createPoolMatches"
  >();

  const divisionStatus = division?.status;
  const isDivisionRunning = divisionStatus === "running";

  const setDivisionStatus = useSetDivisionStatusMutation();
  const completePool = useCompletePoolsMutation();

  function togglePool(poolId: number) {
    const isSelected =
      poolFilterValues.has(poolId) && poolFilterValues.size === 1;
    navigate({
      replace: true,
      resetScroll: false,
      search: (prev) => ({
        ...prev,
        pools: isSelected ? undefined : [poolId],
      }),
    });
  }

  const hasMatches = data?.some((p) => p.matches.length > 0) ?? false;

  if (!hasMatches) {
    return (
      <div className="mx-auto max-w-4xl px-3 py-12">
        {canEdit && division && (
          <div className="mb-4 flex justify-end">
            <Button
              color="primary"
              isDisabled={!data || data.length === 0}
              onPress={() => setActiveModal("createPoolMatches")}
            >
              Create Pool Matches
            </Button>
            <CreatePoolMatchesForm
              tournamentId={id}
              division={division}
              {...modalProps("createPoolMatches")}
            />
          </div>
        )}
        <p className="py-8 text-center text-gray-500">
          No pool matches have been created yet.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="bg-gray-100 px-3 py-6">
        <div className="mx-auto flex max-w-2xl flex-col gap-4">
          <DisclosureGroup defaultExpandedKeys={filterOpen ? ["filter"] : []}>
            <Disclosure
              id="filter"
              onExpandedChange={(v) => setFilterOpen(v)}
              className="flex flex-col space-y-2"
            >
              <Heading>
                <AriaButton
                  slot="trigger"
                  className={title({
                    class:
                      "flex flex-row space-x-2 items-center cursor-pointer",
                    size: "xs",
                  })}
                >
                  <span>Filter</span>
                  {filterOpen ? (
                    <MinusIcon size={18} />
                  ) : (
                    <PlusIcon size={18} />
                  )}
                </AriaButton>
              </Heading>
              <DisclosurePanel className="flex flex-col space-y-2">
                <MultiSelect
                  label="Pools"
                  searchKey="pools"
                  options={poolFilterOptions}
                  values={poolFilterValues}
                />

                <MultiSelect
                  label="Courts"
                  searchKey="courts"
                  options={courtFilterOptions}
                  values={courtFilterValues}
                />

                <Link
                  to={pathname}
                  className={button({ color: "accent", class: "self-end" })}
                >
                  Reset <Undo2Icon size={12} />
                </Link>
              </DisclosurePanel>
            </Disclosure>
          </DisclosureGroup>

          {data && data.length > 1 && (
            <div className="flex flex-wrap items-center gap-2">
              {data.map((pool) => {
                const isSelected = poolFilterValues.has(pool.id);
                const isViewerPool = viewerPoolIds.has(pool.id);
                return (
                  <Button
                    key={pool.id}
                    variant={isSelected ? undefined : "outline"}
                    color={isViewerPool ? "primary" : "secondary"}
                    size="sm"
                    onPress={() => togglePool(pool.id)}
                  >
                    Pool {pool.name.toUpperCase()}
                    {pool.court ? ` — ${pool.court}` : ""}
                  </Button>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {canEdit && division && (
        <div className="mx-auto max-w-4xl px-3 py-4">
          <Toolbar
            aria-label="Game controls"
            className="ml-auto flex w-fit items-center gap-2"
          >
            <Button
              variant={isDivisionRunning ? "outline" : "solid"}
              color={isDivisionRunning ? undefined : "primary"}
              isDisabled={setDivisionStatus.isPending}
              onPress={() =>
                setDivisionStatus.mutate({
                  id: division.id,
                  status: isDivisionRunning ? "paused" : "running",
                })
              }
            >
              {isDivisionRunning ? "Pause Division" : "Start Division"}
            </Button>
            <Button
              variant="outline"
              onPress={() => setActiveModal("createPoolMatches")}
            >
              Recreate Pool Matches
            </Button>
            <Button
              variant="outline"
              onPress={() => setActiveModal("completePools")}
            >
              Verify Pools
            </Button>
          </Toolbar>
          <CreatePoolMatchesForm
            tournamentId={id}
            division={division}
            {...modalProps("createPoolMatches")}
          />
          <CompletePoolsForm
            tournamentId={id}
            division={division}
            {...modalProps("completePools")}
          />
        </div>
      )}
      <div className="mx-auto flex max-w-4xl flex-col gap-12 pb-12">
        {visiblePools.map((pool) => {
          const completedMatches = pool.matches.filter(
            (m) => m.status === "completed",
          ).length;
          const totalMatches = pool.matches.length;

          const isViewerPool = viewerPoolIds.has(pool.id);
          const poolStats = getPoolStats(pool);
          const allNotStarted = pool.matches.every(
            (m) => m.status === "scheduled",
          );
          const allCompleted = pool.matches.every(
            (m) => m.status === "completed",
          );
          const isPoolCompleted = pool.teams.every((t) => t.finish != null);

          return (
            <section
              key={pool.id}
              className="bg-content-background-alt flex flex-col overflow-hidden px-3 md:rounded-lg"
            >
              <div
                className={`bg-content-background -mx-3 flex w-[calc(100%+1.5rem)] flex-wrap items-center justify-between gap-3 px-4 py-3 ${isViewerPool ? "text-blue-700" : ""}`}
              >
                <span
                  className={title({
                    size: "sm",
                    className: "uppercase",
                  })}
                >
                  Pool {pool.name.toUpperCase()}
                  {pool.court ? ` — ${pool.court}` : ""}
                </span>
                <span className="flex flex-wrap items-center gap-2">
                  {canEdit &&
                    (isPoolCompleted ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="whitespace-nowrap normal-case"
                        isDisabled={
                          completePool.isPending &&
                          completePool.variables?.poolIds?.includes(pool.id)
                        }
                        onPress={() =>
                          completePool.mutate({
                            id: tournamentDivisionId,
                            poolIds: [pool.id],
                          })
                        }
                      >
                        Reverify Pool
                      </Button>
                    ) : allNotStarted ? (
                      <span className="text-sm font-medium text-gray-500 normal-case">
                        Not Started
                      </span>
                    ) : !allCompleted ? (
                      <span className="text-sm font-medium text-amber-600 normal-case">
                        In Progress
                      </span>
                    ) : (
                      <Button
                        variant="solid"
                        color="primary"
                        size="sm"
                        className="normal-case"
                        isDisabled={
                          completePool.isPending &&
                          completePool.variables?.poolIds?.includes(pool.id)
                        }
                        onPress={() =>
                          completePool.mutate({
                            id: tournamentDivisionId,
                            poolIds: [pool.id],
                          })
                        }
                      >
                        Verify Pool
                      </Button>
                    ))}
                  <span
                    className={`text-sm font-medium normal-case ${isViewerPool ? "text-blue-600" : "text-gray-500"}`}
                  >
                    {completedMatches}/{totalMatches} complete
                  </span>
                  <DialogTrigger>
                    <Button variant="outline" size="sm" className="normal-case">
                      <ListOrderedIcon className="size-3.5" />
                      Standings
                    </Button>
                    <Popover placement="bottom end">
                      <div className="min-w-[240px] p-3">
                        <p className="mb-2 text-sm font-semibold normal-case">
                          Pool {pool.name.toUpperCase()} Standings
                        </p>
                        {poolStats ? (
                          <div className="flex flex-col gap-1 text-sm normal-case">
                            {pool.teams
                              .toSorted(
                                (a, b) =>
                                  (poolStats[a.teamId]?.rank ?? 99) -
                                  (poolStats[b.teamId]?.rank ?? 99),
                              )
                              .map((pt) => {
                                const stats = poolStats[pt.teamId];
                                const team = teamMap.get(pt.teamId);
                                return (
                                  <div
                                    key={pt.teamId}
                                    className="flex items-center gap-2"
                                  >
                                    <span className="w-4 text-right text-gray-500">
                                      {stats?.rank ?? "-"}
                                    </span>
                                    <span className="flex-1 truncate">
                                      {team ? (
                                        <TeamNames
                                          players={team.team.players}
                                          showFirst={false}
                                          separator="/"
                                          link={false}
                                        />
                                      ) : (
                                        "Unknown"
                                      )}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                      {stats?.wins.size ?? 0}W-
                                      {stats?.losses.size ?? 0}L
                                    </span>
                                  </div>
                                );
                              })}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500 normal-case">
                            No matches completed yet.
                          </p>
                        )}
                      </div>
                    </Popover>
                  </DialogTrigger>
                </span>
              </div>

              <div className="mx-auto flex w-full max-w-xl flex-col content-center space-y-6 px-1 pt-6 pb-8 md:px-3">
                {sortBy(pool.matches, (m) => m.matchNumber).map((match) => (
                  <PoolMatchGrid
                    key={match.id}
                    {...match}
                    teamA={
                      match.teamAId
                        ? (teamMap.get(match.teamAId) ?? null)
                        : null
                    }
                    teamB={
                      match.teamBId
                        ? (teamMap.get(match.teamBId) ?? null)
                        : null
                    }
                    refetch={refetch}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </>
  );
});
